import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { defaultReportError } from '@/composables/useErrorLogger'
import { useDeviceStore } from '@/stores/device'
import { MouseKeys } from '@rapoo/device-sdk'
import { useConfigurationStore } from '@/stores'
import type { Macro } from '@rapoo/device-sdk'

export function useKeySDK() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const configStore = useConfigurationStore()
  const { currentConfig } = storeToRefs(configStore)
  const isLoading = ref(false)

  const configIndex = computed(() => currentConfig.value?.index || 0)

  /**
   * 获取当前 keyMap 数据。
   */
  const initKeys = async (): Promise<MouseKeys.LayerKeysMap> => {
    isLoading.value = true
    const mouseKeys = currentDevice.value?.features.mouseKeys
    if (!mouseKeys) {
      console.warn("Current device doesn't have mouseKeys feature")
      return []
    }

    try {
      return await mouseKeys.getMouseKeyMap()
    } catch (error) {
      console.error('Failed to get mouse keys', error)
      defaultReportError(`Failed to get mouse keys ${JSON.stringify(error)}`)
      return []
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 设置指定键位数据。
   */
  const setKey = async (payload: MouseKeys.keyMapItem) => {
    const mouseKeys = currentDevice.value?.features.mouseKeys
    if (!mouseKeys) {
      console.warn("Current device doesn't have mouseKeys feature")
      return
    }

    try {
      await mouseKeys.setMouseKey(payload, configIndex.value)
    } catch (error) {
      console.error('Failed to set mouse key', error)
      defaultReportError(`Failed to set mouse key ${JSON.stringify(error)}`)
    }
  }

  /** 获取宏列表 */
  const getMacroList = async (): Promise<Macro[]> => {
    const mouseKeys = currentDevice.value?.features.mouseKeys
    if (!mouseKeys) {
      console.warn("Current device doesn't have mouseKeys feature")
      return []
    }
    try {
      return await mouseKeys.getMacroList()
    } catch (error) {
      console.error('Failed to get macro list', error)
      defaultReportError(`Failed to get macro list ${JSON.stringify(error)}`)
      return []
    }
  }

  /** 流式获取宏列表 */
  const getMacroListStream = (batchSize?: number) => {
    const mouseKeys = currentDevice.value?.features.mouseKeys
    if (!mouseKeys) {
      console.warn("Current device doesn't have mouseKeys feature")
      return undefined
    }
    return mouseKeys.getMacroListStream(batchSize)
  }

  /** 获取指定组的宏 */
  const getMacroByIndex = async (index: number): Promise<Macro | undefined> => {
    const mouseKeys = currentDevice.value?.features.mouseKeys
    if (!mouseKeys) {
      console.warn("Current device doesn't have mouseKeys feature")
      return undefined
    }
    try {
      return await mouseKeys.getMacroByIndex(index)
    } catch (error) {
      console.error('Failed to get macro by index', error)
      defaultReportError(`Failed to get macro by index ${JSON.stringify(error)}`)
      return undefined
    }
  }

  /** 设置指定组的宏 */
  const setMacroByIndex = async (macro: Macro, index: number): Promise<void> => {
    const mouseKeys = currentDevice.value?.features.mouseKeys
    if (!mouseKeys) {
      console.warn("Current device doesn't have mouseKeys feature")
      return
    }
    try {
      await mouseKeys.setMacroByIndex(macro, index)
    } catch (error) {
      console.error('Failed to set macro by index', error)
      defaultReportError(`Failed to set macro by index ${JSON.stringify(error)}`)
    }
  }

  /** 获取指定组的压枪宏 */
  // const getPressureMacroByIndex = async (index: number): Promise<PressureMacro | undefined> => {
  //   const mouseKeys = currentDevice.value?.features.mouseKeys
  //   if (!mouseKeys) {
  //     console.warn("Current device doesn't have mouseKeys feature")
  //     return undefined
  //   }
  //   try {
  //     return await mouseKeys.getPressureMacroByIndex(index)
  //   } catch (error) {
  //     console.error('Failed to get pressure macro', error)
  //     defaultReportError(`Failed to get pressure macro ${JSON.stringify(error)}`)
  //     return undefined
  //   }
  // }

  /** 设置指定组的压枪宏 */
  // const setPressureMacroByIndex = async (macro: PressureMacro, index: number): Promise<void> => {
  //   const mouseKeys = currentDevice.value?.features.mouseKeys
  //   if (!mouseKeys) {
  //     console.warn("Current device doesn't have mouseKeys feature")
  //     return
  //   }
  //   try {
  //     await mouseKeys.setPressureMacroByIndex(macro, index)
  //   } catch (error) {
  //     console.error('Failed to set pressure macro', error)
  //     defaultReportError(`Failed to set pressure macro ${JSON.stringify(error)}`)
  //   }
  // }

  return {
    isLoading,
    initKeys,
    setKey,
    getMacroList,
    getMacroListStream,
    getMacroByIndex,
    setMacroByIndex,
    // getPressureMacroByIndex,
    // setPressureMacroByIndex,
  }
}
